import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router'

// ─── Email magic-link verify screen ──────────────────────────────────────────
// Reached at /verify?token=… from the sign-in email. The one-time token is
// traded for a session when the person presses Continue, NOT on mount.
//
// Corporate mail gateways open every link in a headless browser before the
// recipient sees it, and a headless browser runs mount effects. Redeeming on
// mount let the scanner spend the single-use token, so the operator got "This
// link didn't work". The same thing happened on the tenant portal to a SOLIS
// (Greystar) user on 2026-09-03: an AWS-hosted Linux Chrome 124 loaded her
// link 39 seconds after delivery and her account was stamped signed in. See
// tenant-portal/src/components/ConfirmSignInLink.tsx.
//
// On success the parent's `user` flips non-null and the app renders normally
// (the /verify path then falls through to Home). On failure we surface the
// reason and a way back to the sign-in card.

export default function VerifyLink({
  onVerify, loading, error,
}: {
  onVerify: (linkToken: string) => Promise<void>
  loading: boolean
  error: string | null
}) {
  const location = useLocation()
  const navigate = useNavigate()
  const token = new URLSearchParams(location.search).get('token')
  // `error` is shared auth state and may predate this page, so a failure only
  // counts once this page has actually tried the token.
  const [attempted, setAttempted] = useState(false)

  const failed = !token || (attempted && !loading && !!error)
  const busy = attempted && loading

  const onContinue = () => {
    if (!token || busy) return
    setAttempted(true)
    void onVerify(token)
  }

  return (
    <div style={{
      minHeight: '100vh',
      // One gate ground across every product — brand-tokens --color-gate-field.
      background: 'var(--color-gate-field, #F8F7F9)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      fontFamily: "'Montserrat', 'Avenir', sans-serif",
    }}>
      <div style={{
        width: '100%', maxWidth: 440, background: '#ffffff', borderRadius: 16,
        padding: '40px 44px 36px', textAlign: 'center', color: '#231F20',
        border: '1px solid var(--color-gray-300, #ECE9EF)',
        boxShadow: '0 12px 32px rgba(35,31,32,0.10), 0 2px 8px rgba(35,31,32,0.05)',
      }}>
        <img src="/parkify-logo-color.svg" alt="Parkify" style={{ height: 30, marginBottom: 24 }} />
        {failed ? (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 8px', letterSpacing: '-0.4px' }}>
              This link didn't work
            </h1>
            <p style={{ fontSize: 13, color: '#5a5567', lineHeight: 1.6, margin: '0 0 22px' }}>
              {token
                ? (error ?? 'The link is invalid or has expired.')
                : 'This sign-in link is missing its token.'}<br />
              Each sign-in link works once. A requested link expires after 15 minutes, an invite link after 7 days.
            </p>
            <button onClick={() => navigate('/', { replace: true })} style={buttonStyle}>
              Back to sign in
            </button>
          </>
        ) : (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 8px', letterSpacing: '-0.4px' }}>
              {busy ? 'Signing you in…' : 'Continue to your console'}
            </h1>
            <p style={{ fontSize: 13, color: '#5a5567', lineHeight: 1.6, margin: '0 0 22px' }}>
              {busy ? 'One moment while we open your console.' : 'Your sign-in link is ready.'}
            </p>
            <button onClick={onContinue} disabled={busy} style={{ ...buttonStyle, opacity: busy ? 0.7 : 1 }}>
              {busy ? 'Signing you in…' : 'Continue'}
            </button>
          </>
        )}
      </div>
    </div>
  )
}

const buttonStyle = {
  padding: '12px 24px', borderRadius: 'var(--radius-btn)', background: '#7F3F98',
  border: 'none', color: '#fff', fontFamily: 'inherit', fontWeight: 700,
  fontSize: 14, cursor: 'pointer',
} as const
